import React, { useEffect, useCallback } from 'react';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ValidationScope } from '@/components/forms/validation';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { PriceRowEditor, PriceRowEditorFooter } from '@/seller/components/PriceRow';
import { ProjectFeeSet } from '@/types';
import { ExternalAgreementLineCreateOptions } from '@/services/external-agreements';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';

interface ExternalAgreementCreateWizardStepPriceProps {
    value: ExternalAgreementLineCreateOptions[];
    onChange?(data: ExternalAgreementLineCreateOptions[]): void;
    projectFeeSets: ProjectFeeSet[] | null;
}

const ExternalAgreementCreateWizardStepPrice: React.FC<ExternalAgreementCreateWizardStepPriceProps> = ({
    value,
    onChange,
    projectFeeSets,
}) => {
    const activeProject = useSelector((state: IState) => state.global.activeProject!);

    const handleChange = (newItem: ExternalAgreementLineCreateOptions, oldItem: ExternalAgreementLineCreateOptions) => {
        triggerHandler(onChange, arrayReplace(value, oldItem, newItem));
    };

    const handleAdd = useCallback(() => {
        const defaultFeeSet = projectFeeSets!.find((x) => x.isDefault) || projectFeeSets![0];
        triggerHandler(onChange, [
            ...value,
            {
                description: '',
                price: null,
                projectFeeSetId: defaultFeeSet ? defaultFeeSet.id : 0,
                itemCategoryId: undefined,
                tags: [],
            },
        ]);
    }, [projectFeeSets, onChange, value]);

    const handleRemove = (item: ExternalAgreementLineCreateOptions) => {
        triggerHandler(
            onChange,
            value.filter((x) => x !== item)
        );
    };

    useEffect(() => {
        if (projectFeeSets && value.length === 0) {
            handleAdd();
        }
    }, [value, projectFeeSets, handleAdd]);

    if (!projectFeeSets) {
        return <LoadingIndicator />;
    }

    return (
        <ValidationScope>
            <div>
                {value.map((row, i) => (
                    <PriceRowEditor
                        key={i}
                        showLabels={i === 0}
                        projectFeeSets={projectFeeSets}
                        value={row}
                        onChange={(newValue) => handleChange({ ...row, ...newValue }, row)}
                        onDeleteClick={() => handleRemove(row)}
                        rootItemCategoryFilter={(cat) =>
                            activeProject.permissions.categoryPermissions[cat.id]?.canUseInChangeRequests ?? false
                        }
                    />
                ))}
            </div>

            <PriceRowEditorFooter rows={value} onAddRowClick={handleAdd} />
        </ValidationScope>
    );
};

export default ExternalAgreementCreateWizardStepPrice;